import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { apiUrl } from '../../../utils/config';
import { OrderStatusBadge } from '../../shared/OrderStatusBadge';
import Footer from '../components/Footer';

const fetchOrder = async (id) => {
  const { data } = await axios.get(`${apiUrl}/bill/${id}`);
  return data.bill || data.item || data;
};

const fmt = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

export default function OrderSuccess() {
  const { id } = useParams();

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => fetchOrder(id),
    enabled: !!id,
    retry: 1,
  });

  const items = order?.items || [];
  const subtotal = items.reduce(
    (s, i) => s + Number(i.price || 0) * Number(i.quantity || 0),
    0
  );
  const tax = subtotal * 0.1;
  const deliveryFee = subtotal >= 30 ? 0 : 3.99;
  const grandTotal = subtotal + tax + deliveryFee;

  return (
    <>
      <div className="max-w-2xl mx-auto px-4 py-16">
        {/* Success banner */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-4xl mx-auto mb-4 shadow-sm">
            ✅
          </div>
          <h1 className="font-serif text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-gray-400 text-sm">Your order has been placed successfully.</p>
          {id && <p className="text-[#f97316] font-bold mt-2 text-lg">Order #{id}</p>}
        </div>

        {isLoading && (
          <div className="flex justify-center py-10">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!isLoading && order && (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden mb-6">
            {/* Status row */}
            <div className="flex items-center justify-between p-5 border-b border-gray-50">
              <span className="font-bold text-gray-800 text-sm">Order Status</span>
              <OrderStatusBadge status={order.status || 'Pending'} />
            </div>

            {/* Items */}
            <div className="p-5 space-y-4">
              {items.map((item, i) => {
                const product = item.item || item;
                return (
                  <div key={i} className="flex items-center gap-3">
                    {product?.images?.image ? (
                      <img
                        src={product.images.image}
                        alt={product.name}
                        className="w-12 h-12 rounded-xl object-cover flex-shrink-0 shadow-sm"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center text-xl flex-shrink-0">
                        🍽
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-800 truncate">
                        {product?.name || 'Item'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {fmt(item.price)} × {item.quantity}
                      </p>
                    </div>
                    <span className="font-bold text-gray-800 text-sm flex-shrink-0">
                      {fmt(Number(item.price) * Number(item.quantity))}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Price summary */}
            <div className="bg-gray-50 p-5 space-y-2 text-sm border-t border-gray-100">
              <div className="flex justify-between text-gray-500">
                <span>Subtotal</span>
                <span>{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Tax (10%)</span>
                <span>{fmt(tax)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Delivery</span>
                <span>
                  {deliveryFee === 0 ? (
                    <span className="text-green-600 font-semibold">Free</span>
                  ) : (
                    fmt(deliveryFee)
                  )}
                </span>
              </div>
              <div className="flex justify-between font-black text-gray-900 text-base pt-2 border-t border-gray-200">
                <span>Total</span>
                <span className="text-[#f97316]">{fmt(grandTotal)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {id && (
            <Link
              to={`/track/${id}`}
              className="flex-1 bg-[#f97316] text-white py-3 rounded-2xl font-bold text-center hover:bg-orange-600 transition"
            >
              📦 Track Order
            </Link>
          )}
          <Link
            to="/menu"
            className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-2xl font-bold text-center hover:bg-gray-200 transition"
          >
            🍽 Order Again
          </Link>
          <Link
            to="/profile"
            className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-2xl font-bold text-center hover:bg-gray-200 transition"
          >
            👤 My Orders
          </Link>
        </div>
      </div>
      <Footer />
    </>
  );
}
